import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      const user = await resetPassword(token, password);
      toast.success("Password updated. You're signed in.");
      navigate(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="reset-password-page">
      <div className="hidden lg:flex flex-col justify-between bg-navy grain p-14">
        <Logo light />
        <div>
          <h2 className="font-display text-4xl font-bold text-white leading-tight">Choose a new password.</h2>
          <p className="mt-4 text-slate-400">Pick something secure you'll remember.</p>
        </div>
        <p className="text-xs text-slate-500">An independent financial-market education & research platform.</p>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo /></div>
          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <h1 className="font-display text-3xl font-bold text-navy">Invalid link</h1>
              <p className="mt-3 text-slate-500">This reset link is missing its token.</p>
              <Link to="/forgot-password" className="mt-6 inline-block text-navy font-semibold hover:text-gold" data-testid="reset-request-new">Request a new link</Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-navy">Set a new password</h1>
              <p className="mt-2 text-sm text-slate-500">Enter and confirm your new password.</p>
              <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
                <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" placeholder="New password (min 6 chars)" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <input required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" placeholder="Confirm new password" className="w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold" />
                <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60">
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
              <p className="mt-6 text-sm text-slate-500 text-center">
                <Link to="/login" className="text-navy font-medium hover:text-gold" data-testid="go-login">Back to Login</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
